/**
 * OnboardingDrawer
 *
 * Right-side drawer showing an employee's onboarding journey:
 * timeline, health score, probation economics, recommendations,
 * signal breakdown, and sick day stats.
 *
 * Event participation is not shown here (Marie 16.09.2026: "weil sie ja gerade
 * erst gestartet sind"); the health score does not count it either.
 */

import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Circle,
  GraduationCap,
  MessageSquare,
  RefreshCw,
  ShieldAlert,
  UserX,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useInProbezeit } from "../hooks/useProbezeitPerson.js";
import { employeesApi } from "../lib/api.js";
import { arbeitstageText } from "../lib/krankheitstage.js";
import { onboardingGesundheit } from "../lib/onboarding-gesundheit.js";
import { EigeneKrankheitstage } from "./EigeneKrankheitstage.js";
import { ProbezeitWirtschaftBlock } from "./probezeit/ProbezeitWirtschaftBlock.js";
import { RiskBadge } from "./ui/RiskBadge.js";

// ── Helpers ───────────────────────────────────────────────────────────────────

function healthLabel(score: number): { text: string; color: string } {
  if (score >= 80) return { text: "Gut", color: "var(--risk-low)" };
  if (score >= 50) return { text: "Aufmerksamkeit nötig", color: "var(--accent-gold)" };
  return { text: "Kritisch", color: "var(--risk-high)" };
}

function gateStatus(
  tenureMonths: number | null,
  isInactive: boolean,
  gateMonths: number,
): "passed" | "pending" | "failed" | "future" {
  const tenure = tenureMonths ?? 0;
  if (isInactive && tenure < gateMonths) return "failed";
  if (tenure >= gateMonths) return "passed";
  if (tenure > 0) return "pending";
  return "future";
}

const GATE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  passed: { bg: "var(--risk-low-bg)", text: "var(--risk-low)", border: "rgba(34,197,94,0.2)" },
  pending: { bg: "var(--risk-med-bg)", text: "var(--accent-gold)", border: "rgba(245,158,11,0.2)" },
  failed: { bg: "var(--risk-high-bg)", text: "var(--risk-high)", border: "rgba(240,78,78,0.2)" },
  future: { bg: "var(--bg-card)", text: "var(--text-muted)", border: "var(--border)" },
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  employeeId: number | null;
  onClose: () => void;
}

// ── Main Component ────────────────────────────────────────────────────────────

export function OnboardingDrawer({ employeeId, onClose }: Props) {
  const { t } = useTranslation();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Backdrop click
  function handleBackdropClick(e: React.MouseEvent) {
    if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) onClose();
  }

  const {
    data: analysis,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["employee-analysis", employeeId],
    queryFn: () => employeesApi.getAnalysis(employeeId!),
    enabled: employeeId !== null,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  // „seit Eintritt" gilt nur in der Probezeit; die Antwort teilt sich der Hook mit dem Block,
  // es geht also eine Anfrage raus statt zwei. Dass nicht bei jedem Fensterfokus erneut
  // gefragt wird, liegt nicht an dieser Reihenfolge, sondern daran, dass „nicht in der
  // Probezeit" eine Antwort ist und kein Fehler — siehe `useProbezeitPerson`. Erst nach dem
  // Profil zu fragen, hält beide im selben Schritt.
  const inProbezeit = useInProbezeit(analysis?.id ?? null);

  if (employeeId === null) return null;

  const tenure = analysis?.tenureMonths ?? 0;
  const timelinePct = Math.min(100, Math.round((tenure / 12) * 100));
  const isInactive = analysis?.status === "inactive" || analysis?.isConfirmedLeaving === true;

  const gate1 = analysis ? gateStatus(tenure, isInactive, 6) : "future";
  const gate2 = analysis ? gateStatus(tenure, isInactive, 12) : "future";

  const healthScore = analysis ? onboardingGesundheit(analysis) : 0;
  const health = healthLabel(healthScore);

  // Recommendations
  const recommendations: Array<{
    icon: typeof MessageSquare;
    text: string;
    priority: "high" | "medium" | "low";
    color: string;
  }> = [];
  if (analysis) {
    if (tenure >= 4 && tenure <= 6) {
      recommendations.push({
        icon: MessageSquare,
        text: t("onboarding.drawer.probationFeedback"),
        priority: "high",
        color: "var(--risk-high)",
      });
    }
    if (analysis.retentionScore > 0.5) {
      recommendations.push({
        icon: Users,
        text: t("onboarding.drawer.mentor"),
        priority: "high",
        color: "var(--risk-high)",
      });
    }
    if (recommendations.length === 0) {
      recommendations.push({
        icon: Users,
        text: t("onboarding.drawer.mentor"),
        priority: "low",
        color: "var(--risk-low)",
      });
    }
  }

  // Signals — ausgelöste Risiko-Signale getrennt von stabilisierenden (positiven) Faktoren
  const triggered =
    analysis?.signals
      .filter((s) => s.triggered && !s.positive)
      .slice()
      .sort((a, b) => b.weight - a.weight) ?? [];
  const positives = analysis?.signals.filter((s) => s.triggered && s.positive) ?? [];
  const untriggered = analysis?.signals.filter((s) => !s.triggered) ?? [];
  const maxWeight = analysis ? Math.max(...analysis.signals.map((s) => s.weight), 0.01) : 0.01;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: "rgba(0,0,0,0.35)" }}
      onClick={handleBackdropClick}
    >
      <div
        ref={drawerRef}
        className="h-full flex flex-col overflow-hidden"
        style={{
          width: 520,
          background: "var(--bg-base)",
          borderLeft: "1px solid var(--border)",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.12)",
          animation: "onbSlideIn 0.22s ease-out",
        }}
      >
        <style>{`
          @keyframes onbSlideIn {
            from { transform: translateX(100%); opacity: 0; }
            to   { transform: translateX(0);    opacity: 1; }
          }
        `}</style>

        {/* ── Header ── */}
        <div
          className="flex items-start gap-3 px-5 py-4 shrink-0"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          {analysis ? (
            <>
              <div
                className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5"
                style={{ background: "rgba(234,91,11,0.12)", color: "var(--accent)" }}
              >
                <GraduationCap size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className="text-sm font-semibold truncate"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {analysis.displayName}
                  </span>
                  {isInactive && (
                    <span
                      className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold"
                      style={{
                        background: "rgba(239,68,68,0.12)",
                        color: "var(--risk-high)",
                        border: "1px solid rgba(239,68,68,0.3)",
                      }}
                    >
                      <UserX size={9} /> ausgeschieden
                    </span>
                  )}
                </div>
                <div
                  className="flex items-center gap-2 text-[11px]"
                  style={{ color: "var(--text-muted)" }}
                >
                  <span>{analysis.department ?? "—"}</span>
                  {analysis.position && (
                    <>
                      <ChevronRight size={10} />
                      <span>{analysis.position}</span>
                    </>
                  )}
                  <span style={{ opacity: 0.5 }}>{"·"}</span>
                  <span>{analysis.employmentCategory}</span>
                </div>
              </div>
              {analysis.riskLevel && <RiskBadge level={analysis.riskLevel} />}
            </>
          ) : (
            <div className="flex-1">
              <div
                className="h-4 w-32 rounded animate-pulse"
                style={{ background: "var(--bg-hover)" }}
              />
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 transition-colors ml-2 shrink-0"
            style={{ color: "var(--text-muted)", background: "transparent", border: "none" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--bg-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <X size={15} />
          </button>
        </div>

        {/* ── Scroll Body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">
          {/* Loading */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center h-32 gap-2">
              <RefreshCw size={16} className="animate-spin" style={{ color: "var(--accent)" }} />
              <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                Profil wird geladen…
              </span>
            </div>
          )}

          {isError && (
            <div className="flex items-center gap-2 py-4">
              <AlertCircle size={14} style={{ color: "var(--risk-high)" }} />
              <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                Fehler beim Laden des Profils
              </span>
            </div>
          )}

          {analysis && (
            <>
              {/* ── 1. Onboarding Timeline ── */}
              <div>
                <p
                  className="text-xs font-semibold uppercase tracking-wider mb-3"
                  style={{ color: "var(--text-muted)" }}
                >
                  {t("onboarding.drawer.onboardingTimeline")}
                </p>

                {/* Progress bar */}
                <div className="relative mb-2">
                  <div
                    className="h-2 rounded-full overflow-hidden"
                    style={{ background: "var(--bg-surface)" }}
                  >
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${timelinePct}%`,
                        background: "var(--accent)",
                      }}
                    />
                  </div>
                  {/* Gate markers */}
                  <div
                    className="absolute top-0 w-0.5 h-2 rounded"
                    style={{ left: "50%", background: "var(--text-muted)", opacity: 0.6 }}
                  />
                  <div
                    className="absolute top-0 w-0.5 h-2 rounded"
                    style={{
                      left: "100%",
                      transform: "translateX(-2px)",
                      background: "var(--text-muted)",
                      opacity: 0.6,
                    }}
                  />
                </div>

                {/* Labels */}
                <div
                  className="flex justify-between text-[10px]"
                  style={{ color: "var(--text-muted)" }}
                >
                  <span>Eintritt</span>
                  <span>6M</span>
                  <span>12M</span>
                </div>

                {/* Gate cards */}
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {/* Gate 1: Probezeit */}
                  <div
                    className="rounded-lg p-3 text-center"
                    style={{
                      background: GATE_COLORS[gate1].bg,
                      border: `1px solid ${GATE_COLORS[gate1].border}`,
                    }}
                  >
                    <p
                      className="text-[10px] font-medium uppercase tracking-wider mb-1"
                      style={{ color: "var(--text-muted)" }}
                    >
                      {t("onboarding.drawer.gate1")}
                    </p>
                    <p className="text-xs font-semibold" style={{ color: GATE_COLORS[gate1].text }}>
                      {gate1 === "passed"
                        ? t("onboarding.probationPassed")
                        : gate1 === "failed"
                          ? t("onboarding.probationFailed")
                          : t("onboarding.probationPending")}
                    </p>
                  </div>

                  {/* Gate 2: Erstes Jahr */}
                  <div
                    className="rounded-lg p-3 text-center"
                    style={{
                      background: GATE_COLORS[gate2].bg,
                      border: `1px solid ${GATE_COLORS[gate2].border}`,
                    }}
                  >
                    <p
                      className="text-[10px] font-medium uppercase tracking-wider mb-1"
                      style={{ color: "var(--text-muted)" }}
                    >
                      {t("onboarding.drawer.gate2")}
                    </p>
                    <p className="text-xs font-semibold" style={{ color: GATE_COLORS[gate2].text }}>
                      {gate2 === "passed"
                        ? t("onboarding.probationPassed")
                        : gate2 === "failed"
                          ? t("onboarding.probationFailed")
                          : gate2 === "future"
                            ? "—"
                            : t("onboarding.probationPending")}
                    </p>
                  </div>
                </div>
              </div>

              {/* ── 2. Onboarding Health Score ── */}
              <div
                className="rounded-lg p-4"
                style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
              >
                <p
                  className="text-xs font-semibold uppercase tracking-wider mb-3"
                  style={{ color: "var(--text-muted)" }}
                >
                  {t("onboarding.drawer.healthScore")}
                </p>
                <div className="flex items-center gap-4">
                  {/* Circular score ring */}
                  <div className="relative shrink-0" style={{ width: 72, height: 72 }}>
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: `conic-gradient(${health.color} ${healthScore * 3.6}deg, var(--bg-surface) ${healthScore * 3.6}deg)`,
                      }}
                    />
                    <div
                      className="absolute rounded-full flex items-center justify-center"
                      style={{
                        inset: 6,
                        background: "var(--bg-card)",
                      }}
                    >
                      <span className="text-lg font-bold font-mono" style={{ color: health.color }}>
                        {healthScore}
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold mb-1" style={{ color: health.color }}>
                      {health.text}
                    </p>
                    <div
                      className="flex flex-col gap-0.5 text-[10px]"
                      style={{ color: "var(--text-muted)" }}
                    >
                      <span>Risiko-Score: {(analysis.retentionScore * 100).toFixed(0)}%</span>
                      <span>
                        Eigene Krankheitstage (3M):{" "}
                        {arbeitstageText(analysis.sickDayStats?.sickDays3m ?? 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── 2b. Score-History Mini Chart ── */}
              {analysis.scoreHistory && analysis.scoreHistory.length >= 2 ? (
                <div className="mt-2">
                  <div className="text-[10px] mb-1" style={{ color: "var(--text-muted)" }}>
                    Risiko-Verlauf
                  </div>
                  <ResponsiveContainer width="100%" height={60}>
                    <LineChart
                      data={analysis.scoreHistory.map((p) => ({
                        ...p,
                        pct: Math.round(p.score * 100),
                      }))}
                    >
                      <Line
                        type="monotone"
                        dataKey="pct"
                        stroke="var(--accent)"
                        strokeWidth={1.5}
                        dot={false}
                      />
                      <Tooltip formatter={(v: number) => [`${v}%`, "Drop-Risiko"]} />
                      <XAxis dataKey="date" hide />
                      <YAxis hide domain={[0, "auto"]} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-[10px] mt-2" style={{ color: "var(--text-muted)" }}>
                  Risiko-Verlauf im Aufbau — Daten werden täglich gesammelt.
                </p>
              )}

              {/* ── 2c. Wirtschaftlichkeit in der Probezeit (Spezifikation R7) ── */}
              <ProbezeitWirtschaftBlock personioId={analysis.id} />

              {/* ── 3. Recommendations ── */}
              {recommendations.length > 0 && (
                <div>
                  <p
                    className="text-xs font-semibold uppercase tracking-wider mb-3"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {t("onboarding.drawer.recommendations")}
                  </p>
                  <div className="flex flex-col gap-2">
                    {recommendations.map((rec, i) => {
                      const Icon = rec.icon;
                      return (
                        <div
                          key={i}
                          className="flex items-center gap-3 p-3 rounded-lg"
                          style={{
                            background: "var(--bg-card)",
                            border: "1px solid var(--border)",
                          }}
                        >
                          <Icon size={14} style={{ color: rec.color, flexShrink: 0 }} />
                          <span className="flex-1 text-xs" style={{ color: "var(--text-primary)" }}>
                            {rec.text}
                          </span>
                          <span
                            className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded"
                            style={{
                              color: rec.color,
                              background: `${rec.color}15`,
                              border: `1px solid ${rec.color}30`,
                            }}
                          >
                            {rec.priority}
                          </span>
                          <ChevronRight
                            size={12}
                            style={{ color: "var(--text-muted)", flexShrink: 0 }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── 4. Divider ── */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
                <span
                  className="text-[10px] uppercase tracking-wider font-semibold"
                  style={{ color: "var(--text-muted)" }}
                >
                  {t("onboarding.drawer.detailView")}
                </span>
                <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
              </div>

              {/* ── 5. Signal Breakdown ── */}
              <div>
                <p
                  className="text-xs font-semibold uppercase tracking-wider mb-3"
                  style={{ color: "var(--text-muted)" }}
                >
                  Signal-Breakdown
                </p>

                {triggered.length > 0 && (
                  <div className="mb-3">
                    <p
                      className="text-[10px] font-medium mb-1.5 flex items-center gap-1.5"
                      style={{ color: "var(--risk-high)" }}
                    >
                      <ShieldAlert size={10} /> Ausgelöste Signale ({triggered.length})
                    </p>
                    {triggered.map((signal) => {
                      const pct = Math.round((signal.weight / maxWeight) * 100);
                      const color =
                        signal.weight >= 0.2
                          ? "var(--risk-high)"
                          : signal.weight >= 0.1
                            ? "var(--accent)"
                            : "var(--accent-gold)";
                      return (
                        <div
                          key={signal.key}
                          className="flex items-start gap-2 py-1.5"
                          style={{ borderBottom: "1px solid var(--border)" }}
                        >
                          <ShieldAlert size={12} className="mt-0.5 shrink-0" style={{ color }} />
                          <div className="flex-1 min-w-0">
                            <span
                              className="text-[11px] leading-tight block"
                              style={{ color: "var(--text-primary)" }}
                            >
                              {signal.label}
                            </span>
                            <div
                              className="mt-1 h-1 rounded-full overflow-hidden"
                              style={{ background: "var(--bg-surface)" }}
                            >
                              <div
                                className="h-full rounded-full transition-all"
                                style={{ width: `${pct}%`, background: color }}
                              />
                            </div>
                          </div>
                          <span className="text-[10px] font-mono shrink-0 mt-0.5" style={{ color }}>
                            {(signal.weight * 100).toFixed(0)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {positives.length > 0 && (
                  <div className="mb-3">
                    <p
                      className="text-[10px] font-medium mb-1.5 flex items-center gap-1.5"
                      style={{ color: "var(--risk-low)" }}
                    >
                      <CheckCircle2 size={10} /> Stabilisierende Faktoren ({positives.length})
                    </p>
                    {positives.map((signal) => (
                      <div
                        key={signal.key}
                        className="flex items-start gap-2 py-1.5"
                        style={{ borderBottom: "1px solid var(--border)" }}
                      >
                        <CheckCircle2
                          size={12}
                          className="mt-0.5 shrink-0"
                          style={{ color: "var(--risk-low)" }}
                        />
                        <span
                          className="flex-1 text-[11px] leading-tight"
                          style={{ color: "var(--text-primary)" }}
                        >
                          {signal.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {untriggered.length > 0 && (
                  <details>
                    <summary
                      className="text-[10px] cursor-pointer select-none mb-1.5 flex items-center gap-1.5"
                      style={{ color: "var(--text-muted)", listStyle: "none" }}
                    >
                      <CheckCircle2 size={10} style={{ color: "var(--risk-low)" }} />
                      Nicht ausgelöst ({untriggered.length})
                    </summary>
                    {untriggered.map((signal) => (
                      <div
                        key={signal.key}
                        className="flex items-start gap-2 py-1.5"
                        style={{ borderBottom: "1px solid var(--border)" }}
                      >
                        <Circle
                          size={12}
                          className="mt-0.5 shrink-0"
                          style={{ color: "var(--border-light)", opacity: 0.5 }}
                        />
                        <span
                          className="flex-1 text-[11px] leading-tight"
                          style={{ color: "var(--text-muted)", opacity: 0.6 }}
                        >
                          {signal.label}
                        </span>
                        <span
                          className="text-[10px] font-mono shrink-0 mt-0.5"
                          style={{ color: "var(--border-light)", opacity: 0.5 }}
                        >
                          {(signal.weight * 100).toFixed(0)}%
                        </span>
                      </div>
                    ))}
                  </details>
                )}
              </div>

              {/* ── 7. Eigene Krankheitstage (Arbeitstage, ohne „Kind krank") ── */}
              {analysis.sickDayStats && (
                <EigeneKrankheitstage werte={analysis.sickDayStats} seitEintritt={inProbezeit} />
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        {analysis && (
          <div
            className="px-5 py-3 shrink-0 flex items-center justify-end text-[10px]"
            style={{ borderTop: "1px solid var(--border)", color: "var(--text-muted)" }}
          >
            <span>
              Berechnet{" "}
              {new Date(analysis.calculatedAt).toLocaleTimeString("de-DE", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
